import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadFile, postDownload, downloadBase64 } from "@/lib/api";
import { TaalKeuzeDialog, useTaalActie } from "@/components/TaalKeuzeDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PakketStatusBadge, usePakketSync } from "@/components/PakketStatusBadge";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, FileDown, Package, ExternalLink, Trash2, Mail, Printer, Send, Truck, CheckCircle2, Undo2, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSortable } from "@/lib/sortable";
import { usePersistedState } from "@/lib/persist";
import { LaadBalk, TabelLaadRij } from "@/components/LaadIndicator";
import { VerzendingHealthBanner } from "@/components/VerzendingHealthBanner";
import { VerzendResultaatDialog } from "@/components/VerzendResultaatDialog";

export default function Pakbonnen() {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState({});
  const [orgLand, setOrgLand] = useState({});
  const taalKeuze = useTaalActie();
  const [q, setQ] = usePersistedState("pakbonnen:q", "");
  const [tab, setTab] = usePersistedState("pakbonnen:tab", "open");
  const [selected, setSelected] = useState(new Set());
  const [printing, setPrinting] = useState(false);
  const [verzenden, setVerzenden] = useState(false);
  const [verzendResultaat, setVerzendResultaat] = useState(null);
  const [resultaatOpen, setResultaatOpen] = useState(false);
  const [labels, setLabels] = useState({}); // id -> lokaal bewerkte notitie_label
  const [loading, setLoading] = useState(true);

  const labelVal = (p) => (labels[p.id] !== undefined ? labels[p.id] : (p.notitie_label || ""));
  const saveLabel = async (p) => {
    const val = labelVal(p);
    if (val === (p.notitie_label || "")) return;
    try {
      await api.patch(`/pakbonnen/${p.id}`, { notitie_label: val });
      setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, notitie_label: val } : x)));
      toast.success("Notitie label opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: pb }, { data: o }] = await Promise.all([
        api.get("/pakbonnen"), api.get("/organisaties"),
      ]);
      setItems(pb);
      setOrgs(Object.fromEntries(o.map(x => [x.id, x.naam])));
      setOrgLand(Object.fromEntries(o.map(x => [x.id, x.land])));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  usePakketSync(() => load());

  const remove = async (pid) => {
    if (!window.confirm("Pakbon verwijderen?")) return;
    await api.delete(`/pakbonnen/${pid}`);
    toast.success("Verwijderd"); load();
  };

  const markeerVerzonden = async (pid) => {
    if (!window.confirm("Deze pakbon op status 'verzonden' zetten? Gebruik dit als je zelf brengt, via een externe koerier verstuurt, of buiten het CRM om.")) return;
    try {
      await api.post(`/pakbonnen/${pid}/markeer-verzonden?methode=anders_verstuurd`);
      toast.success("Pakbon op verzonden gezet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon status niet wijzigen");
    }
  };

  const printMyParcelLabel = async (pid, pnaam) => {
    const ok = await downloadFile(`/pakbonnen/${pid}/myparcel-label`, `label-${pnaam}-dymo.pdf`);
    if (ok) toast.success("Label gedownload (109 × 159 mm) — open en print op je Dymo 5XL");
    else toast.error("Label ophalen mislukt");
  };

  const [bulkPrinting, setBulkPrinting] = useState(false);
  const bulkPrintLabels = async () => {
    const ids = sortedRows.filter(p => selected.has(p.id) && p.myparcel_shipment_id).map(p => p.id);
    if (!ids.length) { toast.error("Selecteer minstens één verzonden pakbon met MyParcel-label"); return; }
    setBulkPrinting(true);
    const stamp = new Date().toISOString().slice(0,16).replace(/[:T-]/g,"");
    const ok = await postDownload("/pakbonnen/batch-labels", { ids }, `verzendlabels-dymo-${stamp}.pdf`);
    setBulkPrinting(false);
    if (ok) {
      toast.success(`${ids.length} label(s) gedownload (Dymo 5XL 109 × 159 mm)`);
      setSelected(new Set());
    } else {
      toast.error("Bulk-labels ophalen mislukt");
    }
  };

  const markeerOpenstaand = async (pid) => {
    if (!window.confirm("Deze pakbon terugzetten op 'openstaand'? De MyParcel-koppeling (shipment-id, barcode) wordt losgekoppeld zodat je hem opnieuw kunt aanmelden. De zending bij de vervoerder wordt niet ingetrokken.")) return;
    try {
      await api.post(`/pakbonnen/${pid}/markeer-openstaand`);
      toast.success("Pakbon teruggezet op openstaand");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon status niet wijzigen");
    }
  };

  const naam = (p) => p.weergavenaam || p.nummer;
  const statusOf = (p) => p.status || "open";

  const byTab = items.filter(p => statusOf(p) === tab);
  const filtered = byTab.filter(p => {
    if (!q) return true;
    const term = q.toLowerCase();
    return (naam(p) || "").toLowerCase().includes(term)
      || (p.nummer || "").toLowerCase().includes(term)
      || (orgs[p.relatie_id] || "").toLowerCase().includes(term)
      || (p.ordernummer || "").toLowerCase().includes(term);
  });
  const { sortedRows, SortHeader } = useSortable(filtered, "leverdatum", "desc");

  const counts = useMemo(() => ({
    open: items.filter(p => statusOf(p) === "open").length,
    verzonden: items.filter(p => statusOf(p) === "verzonden").length,
  }), [items]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const isSelectable = (p) => tab === "open" || !!p.myparcel_shipment_id;
  const selectableRows = sortedRows.filter(isSelectable);
  const allVisibleSelected = selectableRows.length > 0 && selectableRows.every(p => selected.has(p.id));
  const toggleAll = () => setSelected(prev => {
    if (allVisibleSelected) return new Set();
    return new Set(selectableRows.map(p => p.id));
  });

  const switchTab = (t) => { setTab(t); setSelected(new Set()); };

  const verzendMyParcel = async () => {
    const ids = sortedRows.filter(p => selected.has(p.id)).map(p => p.id);
    if (!ids.length) { toast.error("Selecteer minstens één pakbon"); return; }
    if (ids.length > 20 && !window.confirm(`Je staat op het punt ${ids.length} zendingen aan te melden bij MyParcel. Doorgaan?`)) return;
    setVerzenden(true);
    try {
      const { data } = await api.post("/pakbonnen/batch-verzenden", { ids });
      // Toon resultaat-dialoog met per pakbon status + reden bij falen
      setVerzendResultaat(data);
      setResultaatOpen(true);
      if (data.verzonden > 0) {
        toast.success(`${data.verzonden} zending(en) aangemeld bij MyParcel`);
        // Direct de Dymo-versie (109 × 159 mm) van de labels downloaden.
        const geluktIds = (data.resultaten || []).filter(r => r.status === "gelukt").map(r => r.id);
        if (geluktIds.length) {
          const stamp = new Date().toISOString().slice(0,16).replace(/[:T-]/g,"");
          const ok = await postDownload("/pakbonnen/batch-labels", { ids: geluktIds },
            `verzendlabels-dymo-${stamp}.pdf`);
          if (ok) toast.success(`Labels gedownload (Dymo 5XL 109 × 159 mm)`);
        }
      } else {
        toast.error("Geen zendingen aangemaakt — zie details in het venster");
      }
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aanmelden bij MyParcel mislukt");
    } finally {
      setVerzenden(false);
    }
  };

  const downloadResultaatLabels = () => {
    if (verzendResultaat?.label_pdf_base64) {
      downloadBase64(verzendResultaat.label_pdf_base64, verzendResultaat.bestandsnaam || "verzendlabels.pdf");
    }
  };

  const batchPrint = async () => {
    const ids = sortedRows.filter(p => selected.has(p.id)).map(p => p.id);
    if (!ids.length) { toast.error("Selecteer minstens één pakbon"); return; }
    setPrinting(true);
    const ok = await postDownload("/pakbonnen/batch-pdf", { ids },
      `pakbonnen-batch-${new Date().toISOString().slice(0,10)}.pdf`);
    setPrinting(false);
    if (ok) {
      toast.success(`${ids.length} pakbon(nen) geprint en op 'Verzonden' gezet`);
      setSelected(new Set());
      load();
    }
  };

  const showSelect = tab === "open" || tab === "verzonden";
  const selectedWithLabel = sortedRows.filter(p => selected.has(p.id) && p.myparcel_shipment_id).length;

  return (
    <div className="space-y-6" data-testid="pakbonnen-page">
      <div>
        <div className="text-sm text-muted-foreground">Logistiek</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Pakbonnen</h1>
        <p className="text-sm text-muted-foreground mt-1">{filtered.length} pakbon(nen)</p>
      </div>

      {loading && <LaadBalk label="Pakbonnen laden" />}

      <VerzendingHealthBanner />

      <div className="flex items-center gap-1 border-b border-border">
        {[
          { id: "open", label: "Openstaand", n: counts.open },
          { id: "verzonden", label: "Verzonden", n: counts.verzonden },
        ].map(t => (
          <button key={t.id} onClick={() => switchTab(t.id)} data-testid={`pakbon-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label} <span className="ml-1 text-xs opacity-70">({t.n})</span>
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl p-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Zoek op naam, nummer, organisatie, ordernummer…" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="pakbon-search" />
        </div>
        {tab === "open" && (
          <Button onClick={batchPrint} disabled={printing || selected.size === 0} data-testid="pakbon-batch-print">
            <Printer className="h-4 w-4 mr-1" /> {printing ? "Bezig…" : `Print selectie (PDF)${selected.size ? ` · ${selected.size}` : ""}`}
          </Button>
        )}
        {tab === "open" && (
          <Button variant="secondary" onClick={verzendMyParcel} disabled={verzenden || selected.size === 0} data-testid="pakbon-batch-verzenden">
            <Truck className="h-4 w-4 mr-1" /> {verzenden ? "Aanmelden…" : "Aanmelden bij MyParcel"}
          </Button>
        )}
        {tab === "verzonden" && (
          <Button onClick={bulkPrintLabels} disabled={bulkPrinting || selectedWithLabel === 0}
                  data-testid="pakbon-batch-print-labels"
                  title="Download alle geselecteerde MyParcel-labels als één PDF in Dymo 5XL formaat (109 × 159 mm)">
            <FileDown className="h-4 w-4 mr-1" />
            {bulkPrinting ? "Labels ophalen…" : `Download labels (Dymo 5XL)${selectedWithLabel ? ` · ${selectedWithLabel}` : ""}`}
          </Button>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
            <tr>
              {showSelect && (
                <th className="p-3 w-10">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll}
                    className="h-4 w-4 accent-primary cursor-pointer" data-testid="pakbon-select-all" />
                </th>
              )}
              <SortHeader k="weergavenaam" accessor={(p)=>naam(p)}>Pakbon-naam</SortHeader>
              <SortHeader k="organisatie" accessor={(p)=>orgs[p.relatie_id]||""}>Organisatie</SortHeader>
              <SortHeader k="ordernummer">Order-nr</SortHeader>
              <SortHeader k="leveranciers_id">Leveranciers-ID</SortHeader>
              <th className="p-3 text-left font-medium text-muted-foreground">Notitie label</th>
              <SortHeader k="bedrag" align="right" accessor={(p)=>Number(p.bedrag||0)}>Bedrag</SortHeader>
              <SortHeader k="leverdatum">Leverdatum</SortHeader>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedRows.map(p => (
              <tr key={p.id} className="hover:bg-secondary/40" data-testid={`pakbon-row-${p.id}`}>
                {showSelect && (
                  <td className="p-3">
                    {(tab === "open" || p.myparcel_shipment_id) ? (
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}
                        className="h-4 w-4 accent-primary cursor-pointer" data-testid={`pakbon-select-${p.id}`} />
                    ) : (
                      <input type="checkbox" disabled className="h-4 w-4 opacity-30 cursor-not-allowed"
                             title="Geen MyParcel-label — kan niet gebulkt worden" />
                    )}
                  </td>
                )}
                <td className="p-3 font-mono text-sm">
                  <span className="flex items-center gap-2 flex-wrap">
                    <Package className="h-4 w-4 text-primary shrink-0" />
                    {naam(p)}
                    {statusOf(p) === "verzonden" && !p.pakket_status && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-sans uppercase tracking-wide text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                        <Send className="h-3 w-3" /> Verzonden
                      </span>
                    )}
                    <PakketStatusBadge status={p.pakket_status}
                                        omschrijving={p.pakket_status_omschrijving}
                                        tracktraceUrl={p.tracktrace_url} />
                    {!p.pakket_status && p.tracktrace_url && (
                      <a href={p.tracktrace_url} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1 text-[10px] font-sans text-primary hover:underline"
                         data-testid={`pakbon-tt-${p.id}`}>
                        <Truck className="h-3 w-3" /> {p.myparcel_barcode || "Track & Trace"}
                      </a>
                    )}
                  </span>
                </td>
                <td className="p-3">
                  <Link to={`/organisaties/${p.relatie_id}`} className="text-primary hover:underline">
                    {orgs[p.relatie_id] || "—"}
                  </Link>
                </td>
                <td className="p-3 text-muted-foreground">{p.ordernummer || "—"}</td>
                <td className="p-3 text-muted-foreground">{p.leveranciers_id || "—"}</td>
                <td className="p-3">
                  <Input value={labelVal(p)}
                    onChange={(e) => setLabels((s) => ({ ...s, [p.id]: e.target.value }))}
                    onBlur={() => saveLabel(p)}
                    placeholder="MyParcel-referentie…"
                    className="h-8 text-sm min-w-[160px]" data-testid={`pakbon-notitie-label-${p.id}`} />
                </td>
                <td className="p-3 text-right font-medium">{fmtEUR(p.bedrag)}</td>
                <td className="p-3 text-muted-foreground">{fmtDate(p.leverdatum)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-2">
                    {p.verkoopkans_id && (
                      <Link to={`/verkoopkansen/${p.verkoopkans_id}`}
                        className="inline-flex items-center text-xs text-muted-foreground hover:text-primary"
                        title="Naar verkoopkans">
                        Kans <ExternalLink className="h-3 w-3 ml-0.5" />
                      </Link>
                    )}
                    <button onClick={() => taalKeuze.start(orgLand[p.relatie_id], (taal) => downloadFile(`/pakbonnen/${p.id}/docx?format=pdf&taal=${taal}`, `pakbon-${naam(p)}.pdf`))}
                       className="inline-flex items-center text-muted-foreground hover:text-primary hover:underline text-sm" data-testid={`pakbon-pdf-${p.id}`} title="Download pakbon als PDF">
                      <FileDown className="h-4 w-4 mr-1" /> PDF
                    </button>
                    <button onClick={() => taalKeuze.start(orgLand[p.relatie_id], async (taal) => { try { const { data } = await api.post(`/pakbonnen/${p.id}/verstuur-email`, { taal }); toast.success(`Pakbon ${data.pakbon} gemaild naar ${data.naar}`); } catch(err){ toast.error(err?.response?.data?.detail || "Mailen mislukt"); } })}
                       className="inline-flex items-center text-primary hover:underline text-sm" data-testid={`pakbon-mail-${p.id}`} title="Mail de pakbon naar de organisatie">
                      <Mail className="h-4 w-4 mr-1" /> Mail
                    </button>
                    {p.myparcel_shipment_id && (
                      <button onClick={() => printMyParcelLabel(p.id, naam(p))}
                         className="inline-flex items-center text-primary hover:underline text-sm"
                         data-testid={`pakbon-print-label-${p.id}`}
                         title="Download MyParcel-label als PDF (Dymo 5XL 109 × 159 mm)">
                        <FileDown className="h-4 w-4 mr-1" /> Label
                      </button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary text-muted-foreground"
                                data-testid={`pakbon-menu-${p.id}`} title="Meer acties" aria-label="Meer acties">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onClick={() => taalKeuze.start(orgLand[p.relatie_id], (taal) => downloadFile(`/pakbonnen/${p.id}/docx?taal=${taal}`, `pakbon-${naam(p)}.docx`))} data-testid={`pakbon-docx-${p.id}`}>
                          <FileDown className="h-4 w-4 mr-2" /> Download Word (.docx)
                        </DropdownMenuItem>
                        {statusOf(p) === "open" && (
                          <DropdownMenuItem onClick={() => markeerVerzonden(p.id)}
                                            className="text-emerald-700 focus:text-emerald-800"
                                            data-testid={`pakbon-anders-verstuurd-${p.id}`}>
                            <CheckCircle2 className="h-4 w-4 mr-2" /> Anders verstuurd
                          </DropdownMenuItem>
                        )}
                        {statusOf(p) === "verzonden" && (
                          <DropdownMenuItem onClick={() => markeerOpenstaand(p.id)}
                                            className="text-amber-700 focus:text-amber-800"
                                            data-testid={`pakbon-terug-openstaand-${p.id}`}>
                            <Undo2 className="h-4 w-4 mr-2" /> Terug op openstaand
                          </DropdownMenuItem>
                        )}
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => remove(p.id)}
                                              className="text-destructive focus:text-destructive"
                                              data-testid={`pakbon-verwijder-${p.id}`}>
                              <Trash2 className="h-4 w-4 mr-2" /> Verwijder pakbon
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
            {sortedRows.length === 0 && (
              <TabelLaadRij colSpan={showSelect ? 9 : 8} loading={loading}
                leeg={tab === "verzonden" ? "Nog geen verzonden pakbonnen." : "Geen openstaande pakbonnen."} />
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Tip: selecteer openstaande pakbonnen en gebruik <strong>Print selectie (PDF)</strong> om ze in één bestand te printen — ze worden dan automatisch verzonden. Na het aanmelden bij MyParcel wordt de PDF automatisch gedownload in <strong>Dymo Label Writer 5XL formaat (109 × 159 mm)</strong>; open hem en print naar de Dymo. Maak een pakbon vanuit een gewonnen <Link to="/verkoopkansen" className="text-primary hover:underline">verkoopkans</Link> (fase Deal!).
      </p>
      <TaalKeuzeDialog open={taalKeuze.open} onOpenChange={taalKeuze.setOpen} land={taalKeuze.land} onKies={taalKeuze.bevestig} titel="Kies de taal van de pakbon" />
      <VerzendResultaatDialog open={resultaatOpen} onOpenChange={setResultaatOpen}
        resultaat={verzendResultaat} onDownloadLabels={downloadResultaatLabels} />
    </div>
  );
}
